import { useEffect, useMemo, useState, useRef } from "react";
import ReportTable from "../ReportTable.component";
import BaseReport from "../BaseReport.component";
import ReportModal from "../ReportModal.component";
import ReportService from "../../../services/reports/Report.service";
import { PlanOTBRawData, PlanOTBParsedData } from "../../../interfaces/reports/PlanOTBReport.interface";
import { MoneyDataItem, PercentDataItem } from "../../../interfaces/reports/BaseReport.interface";
import TipContext from "../../../providers/GlobalTip";
import EpochMonthService from "../../../services/EpochMonth.service";

const TODAY_EPOCH_MONTH = EpochMonthService.getTodayEpochMonth();

interface PlanOTBReportProps {
    clientId: string | number;
    epochMonth: number | null;
}

const HIERARCHY_OPTIONS: Record<string, string[]> = {
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
    "Class by Child": ["parentClientClassCode", "classCode"],
    "Class by Child by Location": ["parentClientClassCode", "classCode", "locationCode"],
    "Location by Class by Child": ["locationCode", "parentClientClassCode", "classCode"],
    "Category by Class by Child": ["category", "parentClientClassCode", "classCode"],
};

const parseCsvFirst = (csv: string | null | undefined): number => {
    if (!csv) return 0;
    const first = csv.split(",")[0];
    const num = Number(first);
    return isNaN(num) ? 0 : num;
};
const parseCsvSum = (csv: string | null | undefined, months: number): number => {
    if (!csv) return 0;
    return csv.split(",").slice(0, months).reduce((sum, val) => {
        const num = Number(val);
        return sum + (isNaN(num) ? 0 : num);
    }, 0);
};

const processRawData = (rawData: PlanOTBRawData[], forwardSpan: number, useCost: boolean, trend: number): PlanOTBParsedData[] => {
    if (!rawData || rawData.length === 0) return [];

    return rawData.map((item) => {
        const sales = item.sales;
        const salesTrend = sales / (trend || 1);

        return {
            locationCode: item.locationCode,
            locationName: item.locationName,
            classCode: item.classCode,
            className: item.className,
            category: item.category,
            parentClientClassCode: item.parentClientClassCode ?? null,

            sales: new MoneyDataItem(item.sales),
            salesPlan: new MoneyDataItem(parseCsvFirst(item.plansForwardSales)),
            salesTrend: new MoneyDataItem(salesTrend),
            markdowns: new MoneyDataItem(item.markdowns),
            inflow: new MoneyDataItem(useCost ? item.inflowCost : item.inflowRetail),
            inventory: new MoneyDataItem(useCost ? item.inventoryCurrentCost : item.inventoryCurrent),
            inventoryPlan: new MoneyDataItem(parseCsvFirst(item.plansForwardInventoryRetail)),
            onOrder: new MoneyDataItem(parseCsvSum(useCost ? item.forwardOnOrderCost : item.forwardOnOrderRetail, forwardSpan)),
            otb: new MoneyDataItem(parseCsvSum(useCost ? item.forwardOTBCost : item.forwardOTBRetail, forwardSpan)),

            vsPlanPercent: new PercentDataItem(0),
        };
    });
};

const reapplyPlanCalcs = (data: PlanOTBParsedData[], useTrendedSales: boolean = false): PlanOTBParsedData[] => {
    return data.map((item) => {
        const sales = Number((useTrendedSales ? item.salesTrend : item.sales)?.value) || 0;
        const salesPlan = Number(item.salesPlan?.value) || 0;
        const vsPlanPercent = salesPlan > 0 ? Math.round(((sales - salesPlan) / salesPlan) * 100) : 0;

        return {
            ...item,
            vsPlanPercent: new PercentDataItem(vsPlanPercent),
        };
    });
};

type MetricType = "sales" | "inventory" | "inflow" | "markdowns" | "onOrder" | "otb";

const ALL_METRICS: MetricType[] = ["sales", "inventory", "inflow", "markdowns", "onOrder", "otb"];

const METRIC_LABELS: Record<MetricType, string> = {
    sales: "Sales",
    inventory: "Inventory",
    inflow: "Inflow",
    markdowns: "Markdowns",
    onOrder: "On Order",
    otb: "OTB",
};

const PRESET_METRICS: Record<string, MetricType[]> = {
    Detailed: [...ALL_METRICS],
    Basic: ["sales", "inventory", "inflow"],
    OTB: ["otb"],
};

const buildAllColumns = (useTrendedSales: boolean): Array<{ name: string; sortable: boolean; metric: MetricType; value: (item: PlanOTBParsedData) => string | number }> => [
    { name: useTrendedSales ? "Sales (Trended)" : "Sales", sortable: true, metric: "sales", value: (item) => (useTrendedSales ? item.salesTrend : item.sales)?.formatted ?? "" },
    { name: "Sales Plan", sortable: true, metric: "sales", value: (item) => item.salesPlan?.formatted ?? "" },
    { name: "Sales Trend", sortable: true, metric: "sales", value: (item) => item.salesTrend?.formatted ?? "" },
    { name: "Vs Plan %", sortable: true, metric: "sales", value: (item) => item.vsPlanPercent?.formatted ?? "" },
    { name: "Markdowns", sortable: true, metric: "markdowns", value: (item) => item.markdowns?.formatted ?? "" },
    { name: "Inflow", sortable: true, metric: "inflow", value: (item) => item.inflow?.formatted ?? "" },
    { name: "Inventory", sortable: true, metric: "inventory", value: (item) => item.inventory?.formatted ?? "" },
    { name: "Inventory Plan", sortable: true, metric: "inventory", value: (item) => item.inventoryPlan?.formatted ?? "" },
    { name: "On Order", sortable: true, metric: "onOrder", value: (item) => item.onOrder?.formatted ?? "" },
    { name: "OTB", sortable: true, metric: "otb", value: (item) => item.otb?.formatted ?? "" },
];

const PlanOTBReport = ({ clientId, epochMonth }: PlanOTBReportProps) => {
    const globalTip = TipContext.useMessage();
    const dataRef = useRef<PlanOTBRawData[]>([]);
    const parentDataRef = useRef<PlanOTBRawData[]>([]);
    const [parsedData, setParsedData] = useState<PlanOTBParsedData[]>([]);
    const [parentParsedData, setParentParsedData] = useState<PlanOTBParsedData[]>([]);
    const [sortedData, setSortedData] = useState<PlanOTBParsedData[]>([]);
    const [loading, setLoading] = useState(true);
    const [hierarchy, setHierarchy] = useState<string[] | null>(["classCode"]);
    const [pricingType, setPricingType] = useState<"Retail" | "Cost">("Retail");
    const [forwardSpan, setForwardSpan] = useState<number>(3);
    const [customTrend, setCustomTrend] = useState<number>(1);
    const [useTrendedSales, setUseTrendedSales] = useState<boolean>(false);
    const [displayMode, setDisplayMode] = useState<"Detailed" | "Basic" | "OTB">("Detailed");
    const [visibleMetrics, setVisibleMetrics] = useState<Set<MetricType>>(new Set(PRESET_METRICS.Detailed));

    const [locationFilter, setLocationFilter] = useState<string[]>([]);
    const [classFilter, setClassFilter] = useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

    const handleDisplayModeChange = (mode: "Detailed" | "Basic" | "OTB") => {
        setDisplayMode(mode);
        setVisibleMetrics(new Set(PRESET_METRICS[mode] ?? PRESET_METRICS.Detailed));
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            ReportService.getPlanOTBReportData({ clientId, epochMonth, includeChildClasses: true }).then((response) => {
                if (response) {
                    dataRef.current = response.data ?? [];
                    parentDataRef.current = response.parentLevelData ?? [];

                    let maxTrend = 0;
                    (response.data ?? []).forEach((item: PlanOTBRawData) => {
                        if (item.trend && item.trend > maxTrend) {
                            maxTrend = item.trend;
                        }
                    });
                    setCustomTrend(maxTrend);

                    const processed = processRawData(response.data ?? [], forwardSpan, pricingType === "Cost", maxTrend);
                    const processedParent = processRawData(response.parentLevelData ?? [], forwardSpan, pricingType === "Cost", maxTrend);
                    setParsedData(processed);
                    setParentParsedData(processedParent);

                    const useParent = hierarchy?.includes("parentClientClassCode");
                    let sorted = ReportService.groupByHierarchy(useParent ? processedParent : processed, hierarchy ?? ["classCode"]);
                    sorted = reapplyPlanCalcs(sorted, useTrendedSales);
                    setSortedData(sorted);
                } else {
                    dataRef.current = [];
                    parentDataRef.current = [];
                    setParsedData([]);
                    setParentParsedData([]);
                    setSortedData([]);
                }
                setLoading(false);
            }).catch(() => {
                if (globalTip) globalTip.show("Failed to fetch data");
                setLoading(false);
            });
        };
        fetchData();
    }, [clientId, epochMonth]);

    useEffect(() => {
        const newParsedData = processRawData(dataRef.current, forwardSpan, pricingType === "Cost", customTrend);
        const newParentParsedData = processRawData(parentDataRef.current, forwardSpan, pricingType === "Cost", customTrend);
        setParsedData(newParsedData);
        setParentParsedData(newParentParsedData);
    }, [forwardSpan, pricingType, customTrend]);

    useEffect(() => {
        const useParent = hierarchy?.includes("parentClientClassCode");
        const sourceData = useParent ? parentParsedData : parsedData;

        if (sourceData.length > 0) {
            let newSortedData = ReportService.sortAndFilterData(
                sourceData,
                hierarchy ?? ["classCode"],
                locationFilter,
                classFilter,
                categoryFilter
            );
            newSortedData = reapplyPlanCalcs(newSortedData, useTrendedSales);
            setSortedData(newSortedData);
        }
    }, [hierarchy, parsedData, parentParsedData, locationFilter, classFilter, categoryFilter, useTrendedSales]);

    let hierarchyColumns = [];
    for (let i = 0; i < (hierarchy?.length ?? 0); i++) {
        hierarchyColumns.push({
            name: "",
            sortable: false,
            customClassName: i === 0
                ? "px-4 py-2 bg-gray-500 text-white"
                : "px-4 py-2 bg-purple-100 border-purple-700 text-purple-800"
        });
    }

    const allColumns = useMemo(() => buildAllColumns(useTrendedSales), [useTrendedSales]);

    const columns = [
        ...hierarchyColumns,
        ...allColumns.filter((col) => visibleMetrics.has(col.metric)).map(({ name, sortable }) => ({ name, sortable })),
    ];

    const tableData = useMemo(() => {
        const visibleColumns = allColumns.filter((col) => visibleMetrics.has(col.metric));
        return sortedData.map((item) => {
            const labelRows = ReportService.hierarchyItemToVerticalDisplayNames(item, hierarchy ?? ["classCode"]);
            return [
                ...labelRows,
                ...visibleColumns.map((col) => col.value(item)),
            ];
        });
    }, [sortedData, hierarchy, visibleMetrics, allColumns]);

    const rowClassName = (rowIndex: number, sortedData?: Array<Array<string | number>>) => {
        const source = sortedData ?? tableData;
        if (!source || rowIndex < 0 || rowIndex >= source.length) return null;
        return ReportService.getRowClassName(source[rowIndex], hierarchy ?? ["classCode"]);
    };

    const nonNullEpochMonth = epochMonth ?? TODAY_EPOCH_MONTH;

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center">
            <h2 className="text-2xl font-bold mb-4">{`Plan & OTB Report - ${EpochMonthService.epochMonthToMMMYY(nonNullEpochMonth)}`}</h2>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="h-full max-w-[1400px]">
                    <BaseReport
                        title="Plan & OTB Report"
                        tableColumns={columns.map((col) => col.name)}
                        tableData={tableData}
                        fileName={`Plan_OTB_Report_${new Date().toLocaleDateString()}`}
                        setHierarchy={setHierarchy}
                        hierarchy={hierarchy}
                        hierarchyOptions={HIERARCHY_OPTIONS}
                        customTrend={customTrend}
                        setCustomTrend={setCustomTrend}

                        parsedData={parsedData}
                        filteredLocationCodes={locationFilter}
                        filteredClassCodes={classFilter}
                        setFilteredLocationCodes={setLocationFilter}
                        setFilteredClassCodes={setClassFilter}
                        filteredCategories={categoryFilter}
                        setFilteredCategories={setCategoryFilter}

                        onApplyFilters={() => {
                            const processed = processRawData(dataRef.current, forwardSpan, pricingType === "Cost", customTrend);
                            setParsedData(processed);
                        }}
                    />

                    <div className="flex flex-row flex-wrap items-center space-x-4 mb-4 ml-8">
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Mode</span>
                            <div className="h-10 flex items-center space-x-1 bg-gray-100 rounded-md p-1">
                                {(["Detailed", "Basic", "OTB"] as const).map((option) => (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => handleDisplayModeChange(option)}
                                        className={`px-3 py-1 text-[13px] font-medium rounded ${displayMode === option ? "bg-white shadow text-gray-900" : "text-gray-500"}`}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Pricing Type</span>
                            <div className="h-10 flex items-center space-x-1 bg-gray-100 rounded-md p-1">
                                {(["Retail", "Cost"] as const).map((option) => (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => setPricingType(option)}
                                        className={`px-3 py-1 text-[13px] font-medium rounded ${pricingType === option ? "bg-white shadow text-gray-900" : "text-gray-500"}`}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">On Order / OTB Span (Months)</span>
                            <input
                                type="number"
                                value={forwardSpan}
                                onChange={e => setForwardSpan(Math.max(1, Number(e.target.value) || 1))}
                                min={1}
                                max={12}
                                step={1}
                                className="h-10 w-32 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">&nbsp;</span>
                            <label className="h-10 flex items-center gap-1.5 cursor-pointer text-[13px] font-medium text-gray-700">
                                <input
                                    type="checkbox"
                                    className="rounded border-gray-300 h-3.5 w-3.5"
                                    checked={useTrendedSales}
                                    onChange={(e) => setUseTrendedSales(e.target.checked)}
                                />
                                Use Trended Sales (Comparison &amp; Data)
                            </label>
                        </div>
                    </div>

                    <div className="flex flex-row flex-wrap items-center space-x-5 mb-4 ml-8 text-xs text-gray-600">
                        <span className="text-gray-400">Show:</span>
                        {ALL_METRICS.map((metric) => (
                            <label key={metric} className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="rounded border-gray-300 h-3.5 w-3.5"
                                    checked={visibleMetrics.has(metric)}
                                    onChange={(e) => {
                                        setVisibleMetrics((prev) => {
                                            const next = new Set(prev);
                                            if (e.target.checked) next.add(metric); else next.delete(metric);
                                            return next;
                                        });
                                    }}
                                />
                                {METRIC_LABELS[metric]}
                            </label>
                        ))}
                    </div>

                    <ReportTable
                        columns={columns}
                        data={tableData}
                        rowClassName={rowClassName}
                        defaultSortColumn={columns[hierarchyColumns.length]?.name ?? "Sales"}
                        defaultSortDirection="desc"
                        headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
                        toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
                        toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
                        stickyHeader={true}
                        hierarchyDepth={hierarchy ? hierarchy.length : 0}
                    />
                </div>
            )}
        </div>
    );
};

interface PlanOTBReportModalProps {
    clientId: string | number;
    epochMonth: number | null;
    onClose: () => void;
}

const PlanOTBReportModal = ({ clientId, epochMonth, onClose }: PlanOTBReportModalProps) => {
    return (
        <ReportModal
            onClose={onClose}
        >
            <PlanOTBReport clientId={clientId} epochMonth={epochMonth} />
        </ReportModal>
    );
};

export { PlanOTBReport, PlanOTBReportModal };
